import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { ConfirmationModal } from '@/components/core';
import { useToast } from '@/hooks/use-toast';
import {
  DownloadSimpleIcon,
  Export,
  FolderMinusIcon,
  ListPlusIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ProhibitIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContactsColumnConfigDropdown } from '../components/contacts-column-config/contacts-column-config-dropdown';
import {
  ContactsFiltersDropdown,
  getChannelFilterChips,
  type ContactsFiltersDraft,
} from '../components/contacts-filters-dropdown/contacts-filters-dropdown';
import {
  ContactsTableActionsCell,
  renderContactsTableCell,
  type ContactsTableCellContext,
} from '../components/contacts-table/contacts-table-cell';
import { ContactsTableSkeletonRows } from '../components/contacts-table/contacts-table-skeleton-rows';
import { ContactsTableStateRow } from '../components/contacts-table/contacts-table-state-row';
import { ContactDrawer } from '../components/contact-drawer/contact-drawer';
import { AddToListModal } from '../components/add-to-list-modal/add-to-list-modal';
import { ListMembershipJobProgress } from '../components/list-membership-job-progress/list-membership-job-progress';
import { AdvancedFiltersDialog } from '../components/advanced-filters-dialog/advanced-filters-dialog';
import { ContactsPagination } from '../components/contacts-pagination/contacts-pagination';
import { ExportContactsDialog } from '../components/export-contacts-dialog/export-contacts-dialog';
import type {
  FilterMatchType,
  FilterRow,
} from '../components/filter-row-builder/filter-row-builder';
import {
  CONTACTS_TABLE_CHECKBOX_STICKY_CLASS,
  getContactsTableBodyCellClass,
  getContactsTableHeaderCellClass,
} from '../config/contacts-table-columns';
import { getFilterableField } from '../config/filterable-fields';
import { useFilterableFields } from '../hooks/use-filterable-fields';
import { useCustomFieldDefinitions } from '../hooks/use-custom-field-definitions';
import { useContactsTableColumns } from '../hooks/use-contacts-table-columns';
import { useDeleteContact, useDeleteContacts, useSearchContacts } from '../hooks/use-contacts';
import { useBulkCreateSuppressions } from '../hooks/use-suppressions';
import { useGetLists, useRemoveListMembers } from '@/modules/lists/hooks/use-lists';
import type { CampaignList } from '@/modules/lists/types/lists.types';
import {
  buildDefaultContactsFilters,
  DEFAULT_CONTACTS_FILTERS,
  getContactsFilterChips,
  hasActiveContactsFilters,
  type ContactsFilters,
} from '../types/contacts-filters.types';
import type { Contact } from '../types/contacts.types';
import { getContactDisplayName, getContactLists } from '../utils/contact-attributes';
import {
  buildContactsRestFilterValues,
  partitionEmailStatusFilters,
} from '../utils/contacts-rest-filter.utils';

export const ContactsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const registry = useFilterableFields();
  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();
  const customFieldDefinitions = useMemo(
    () => customFieldDefinitionsData?.items ?? [],
    [customFieldDefinitionsData?.items]
  );

  const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [isAddToListOpen, setIsAddToListOpen] = useState(false);
  const [isRemoveFromListConfirmOpen, setIsRemoveFromListConfirmOpen] = useState(false);
  const [isBulkRemoveFromList, setIsBulkRemoveFromList] = useState(false);
  const [actionContact, setActionContact] = useState<Contact | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(() => new Set());
  // "Select all matching": every contact the filter matches, not just this page. Rows the user
  // unticks afterwards are kept as exclusions — only on the page in view, since leaving the page
  // ends the mode, the same as it clears an ordinary selection.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [excludedContactIds, setExcludedContactIds] = useState<Set<string>>(() => new Set());
  const [membershipJob, setMembershipJob] = useState<{ jobId: string } | null>(null);
  const { mutateAsync: bulkCreateSuppressions, isPending: isBulkSuppressing } =
    useBulkCreateSuppressions();
  const bulkSuppressIdempotencyKeyRef = useRef<string | null>(null);
  const bulkSuppressInFlightRef = useRef(false);
  const [filters, setFilters] = useState<ContactsFilters>(DEFAULT_CONTACTS_FILTERS);
  const [activeList, setActiveList] = useState<CampaignList | null>(null);
  const [search, setSearch] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [emailStatusFilters, setEmailStatusFilters] = useState<string[]>([]);
  const [smsStatusFilters, setSmsStatusFilters] = useState<string[]>([]);
  const [isBulkSuppressConfirmOpen, setIsBulkSuppressConfirmOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const [advancedRows, setAdvancedRows] = useState<FilterRow[]>([
    { id: '1', fieldKey: '', operator: '', value: '' },
  ]);
  const [advancedMatchType, setAdvancedMatchType] = useState<FilterMatchType>('all');
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const { visibleColumns, toggleableColumns, columnVisibility, toggleColumnVisibility } =
    useContactsTableColumns(customFieldDefinitions);

  const filterableCustomFields = useMemo(
    () => registry.filter((field) => field.storageKind === 'customAttribute'),
    [registry]
  );

  const { data: listsData } = useGetLists({
    pageNo: 1,
    pageSize: 100,
  });
  const listsCount = listsData?.totalCount ?? 0;
  const listNamesById = useMemo(
    () =>
      Object.fromEntries(
        (listsData?.items ?? []).map((list: CampaignList) => [list.id, list.name.trim()])
      ),
    [listsData?.items]
  );

  const advancedFilterRowCount = advancedRows.filter(
    (row) => row.fieldKey && row.operator && (row.operator === 'isEmpty' || row.value.trim())
  ).length;

  const { mutateAsync: deleteContact, isPending: isDeletingContact } = useDeleteContact();
  const { mutateAsync: deleteContacts, isPending: isDeletingContacts } = useDeleteContacts();
  const { mutateAsync: removeListMembers, isPending: isRemovingFromListAction } =
    useRemoveListMembers();

  const emailStatusSearchParams = useMemo(
    () => partitionEmailStatusFilters(emailStatusFilters),
    [emailStatusFilters]
  );

  const { data, isLoading, isError, isFetching, refetch } = useSearchContacts(
    search,
    {
      pageNo,
      pageSize,
    },
    {
      emailStatusFilters: emailStatusSearchParams.emailStatusFilters,
      smsStatusFilters,
      listId: filters.listName ? activeList?.id : undefined,
      fieldFilters: filters,
      advancedRows,
      advancedMatchType,
      registry,
      suppressed: emailStatusSearchParams.suppressed,
    }
  );

  const contacts = useMemo(() => data?.items ?? [], [data?.items]);
  const visibleContacts = contacts;
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const filterChips = getContactsFilterChips(filters, registry);
  const filtersActive = hasActiveContactsFilters(filters, registry);
  const isListFilterActive = Boolean(filters.listName && activeList);
  const pageContactIds = useMemo(
    () => visibleContacts.map((contact) => contact.ItemId),
    [visibleContacts]
  );
  const isContactSelected = (contactId: string) =>
    selectAllMatching ? !excludedContactIds.has(contactId) : selectedContactIds.has(contactId);
  const selectedCount = selectAllMatching
    ? Math.max(0, totalCount - excludedContactIds.size)
    : selectedContactIds.size;
  const allPageSelected =
    pageContactIds.length > 0 && pageContactIds.every((id) => isContactSelected(id));
  const somePageSelected = pageContactIds.some((id) => isContactSelected(id)) && !allPageSelected;
  const canSelectAllMatching =
    !selectAllMatching && allPageSelected && totalCount > pageContactIds.length;

  useEffect(() => {
    setFilters((prev) => {
      const nextFields = { ...prev.fields };
      let changed = false;

      for (const field of filterableCustomFields) {
        if (!(field.key in nextFields)) {
          nextFields[field.key] = field.defaultValue ?? '';
          changed = true;
        }
      }

      return changed ? { ...prev, fields: nextFields } : prev;
    });
  }, [filterableCustomFields]);

  useEffect(() => {
    setPageNo(1);
    setSelectedContactIds(new Set());
    setSelectAllMatching(false);
    setExcludedContactIds(new Set());
  }, [search, filters, emailStatusFilters, smsStatusFilters, advancedRows, advancedMatchType]);

  useEffect(() => {
    if (totalPages > 0 && pageNo > totalPages) {
      setPageNo(totalPages);
    }
  }, [pageNo, totalPages]);

  useEffect(() => {
    setSelectedContactIds(new Set());
    setSelectAllMatching(false);
    setExcludedContactIds(new Set());
  }, [pageNo, pageSize]);

  const handlePageSizeChange = (nextPageSize: number) => {
    // Ensure revisiting a page size still hits the network (query cache can otherwise stay fresh).
    queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === 'contacts' &&
          key[1] === 'rest' &&
          key[3] === nextPageSize
        );
      },
    });
    setPageSize(nextPageSize);
    setPageNo(1);
  };

  const handleRemoveFilter = (fieldKey: string) => {
    if (fieldKey === 'listName') {
      setActiveList(null);
      setFilters((prev) => ({
        ...prev,
        listName: '',
        listLeadIds: [],
        listCustomFilter: undefined,
      }));
      return;
    }

    const field = registry.find((entry) => entry.key === fieldKey) ?? getFilterableField(fieldKey);
    setFilters((prev) => ({
      ...prev,
      fields: { ...prev.fields, [fieldKey]: field?.defaultValue ?? '' },
    }));
  };

  const handleApplyFilters = (draft: ContactsFiltersDraft) => {
    setEmailStatusFilters(draft.emailStatuses);
    setSmsStatusFilters(draft.smsStatuses);

    const nextFieldFilters = { ...filters.fields };
    for (const field of filterableCustomFields) {
      nextFieldFilters[field.key] = draft.fieldFilters[field.key] ?? field.defaultValue ?? '';
    }

    if (!draft.listName) {
      setActiveList(null);
      setFilters((prev) => ({
        ...prev,
        listName: '',
        listLeadIds: [],
        listCustomFilter: undefined,
        fields: nextFieldFilters,
      }));
      return;
    }

    const list = listsData?.items.find((entry) => entry.name.trim() === draft.listName);
    if (list) {
      const nextListName = list.name.trim() || 'Untitled list';

      setActiveList(list);
      setFilters((prev) => ({
        ...prev,
        fields: { ...prev.fields, metaTag: '', ...nextFieldFilters },
        listName: nextListName,
        listLeadIds: [],
        listCustomFilter: undefined,
      }));
      return;
    }

    setActiveList(null);
    setFilters((prev) => ({
      ...prev,
      listName: draft.listName,
      listLeadIds: [],
      listCustomFilter: undefined,
      fields: nextFieldFilters,
    }));
  };

  const handleClearFilters = () => {
    setActiveList(null);
    setFilters(buildDefaultContactsFilters(registry));
    setEmailStatusFilters([]);
    setSmsStatusFilters([]);
  };

  const handleClearAllQuery = () => {
    setSearch('');
    setAdvancedRows([{ id: '1', fieldKey: '', operator: '', value: '' }]);
    setAdvancedMatchType('all');
    handleClearFilters();
  };

  const channelFilterChips = getChannelFilterChips(emailStatusFilters, smsStatusFilters);
  const activeFilterCount = filterChips.length + channelFilterChips.length + advancedFilterRowCount;

  const exportSnapshot = useMemo(
    () => ({
      q: search.trim() || undefined,
      filter: buildContactsRestFilterValues({
        emailStatusFilters: emailStatusSearchParams.emailStatusFilters,
        smsStatusFilters,
        listId: filters.listName ? activeList?.id : undefined,
        fieldFilters: filters,
        advancedRows,
        registry,
      }),
      ...(emailStatusSearchParams.suppressed !== undefined
        ? { suppressed: emailStatusSearchParams.suppressed }
        : {}),
    }),
    [
      search,
      emailStatusSearchParams.emailStatusFilters,
      emailStatusSearchParams.suppressed,
      smsStatusFilters,
      filters,
      activeList?.id,
      advancedRows,
      registry,
    ]
  );

  const openBulkSuppress = () => {
    if (selectedCount === 0) return;
    bulkSuppressIdempotencyKeyRef.current =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `bulk-suppress-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setIsBulkSuppressConfirmOpen(true);
  };

  const toggleContactSelection = (contactId: string, checked: boolean) => {
    if (selectAllMatching) {
      setExcludedContactIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.delete(contactId);
        } else {
          next.add(contactId);
        }
        return next;
      });
      return;
    }

    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(contactId);
      } else {
        next.delete(contactId);
      }
      return next;
    });
  };

  const toggleAllPageSelection = (checked: boolean) => {
    if (selectAllMatching) {
      // Unticking the header under "select all" ends it, as in a mail client; ticking it re-includes
      // the page's unticked rows.
      if (checked) {
        setExcludedContactIds(new Set());
      } else {
        clearSelection();
      }
      return;
    }

    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        pageContactIds.forEach((id) => next.add(id));
      } else {
        pageContactIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const selectAllMatchingContacts = () => {
    setSelectAllMatching(true);
    setExcludedContactIds(new Set());
  };

  const clearSelection = () => {
    setSelectedContactIds(new Set());
    setSelectAllMatching(false);
    setExcludedContactIds(new Set());
    setActionContact(null);
    setIsBulkAction(false);
  };

  const openBulkAddToList = () => {
    if (selectedCount === 0) return;
    setIsBulkAction(true);
    setActionContact(null);
    setIsAddToListOpen(true);
  };

  const openEditContact = (contact: Contact) => {
    setContactToEdit(contact);
  };

  const copyContactId = useCallback(
    async (contact: Contact) => {
      try {
        await navigator.clipboard.writeText(contact.ItemId);
        toast({ variant: 'success', title: 'Contact ID copied' });
      } catch {
        toast({ variant: 'destructive', title: 'Could not copy contact ID' });
      }
    },
    [toast]
  );

  const openDeleteContact = (contact: Contact) => {
    setContactToDelete(contact);
    setIsDeleteConfirmOpen(true);
  };

  const handleBulkSuppress = async () => {
    if (selectedContactIds.size === 0 || bulkSuppressInFlightRef.current || isBulkSuppressing) {
      return;
    }

    const selectedContacts = Array.from(selectedContactIds).map(
      (contactId) =>
        visibleContacts.find((entry) => entry.ItemId === contactId) ??
        ({ ItemId: contactId } as Contact)
    );

    const entries = selectedContacts
      .map((contact) => contact.Email?.trim())
      .filter((address): address is string => Boolean(address))
      .map((address) => ({ address, channel: 'email' as const }));

    if (entries.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Unable to suppress contacts',
        description: 'Selected contacts have no email address to suppress.',
      });
      return;
    }

    bulkSuppressInFlightRef.current = true;
    try {
      const result = await bulkCreateSuppressions({
        entries,
        idempotencyKey:
          bulkSuppressIdempotencyKeyRef.current ??
          (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `bulk-suppress-${Date.now()}-${Math.random().toString(36).slice(2)}`),
      });
      const { created, skipped, failed } = result.totals;

      if (failed > 0) {
        toast({
          variant: 'destructive',
          title: 'Some suppressions failed',
          description: `${failed} failed${created + skipped > 0 ? `; ${created} created, ${skipped} skipped` : ''}.`,
        });
      } else {
        toast({
          variant: 'success',
          title: 'Contacts suppressed',
          description: `${created} created, ${skipped} skipped.`,
        });
      }

      clearSelection();
      setIsBulkSuppressConfirmOpen(false);
      bulkSuppressIdempotencyKeyRef.current = null;
      void refetch();
    } catch (suppressError) {
      toast({
        variant: 'destructive',
        title: 'Failed to suppress contacts',
        description: suppressError instanceof Error ? suppressError.message : 'Please try again.',
      });
    } finally {
      bulkSuppressInFlightRef.current = false;
    }
  };

  const openBulkDelete = () => {
    if (selectedCount === 0) return;
    setIsBulkDeleteConfirmOpen(true);
  };

  const handleBulkDelete = async () => {
    const contactIds = Array.from(selectedContactIds);
    if (contactIds.length === 0) return;

    try {
      await deleteContacts({ contactIds });
      toast({
        variant: 'success',
        title: 'Contacts deleted',
        description: `${contactIds.length} contact${contactIds.length === 1 ? '' : 's'} deleted.`,
      });
      clearSelection();
      setIsBulkDeleteConfirmOpen(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete contacts',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;

    try {
      await deleteContact({ contactId: contactToDelete.ItemId });
      toast({
        variant: 'success',
        title: 'Contact deleted',
        description: `${getContactDisplayName(contactToDelete)} was deleted.`,
      });
      setSelectedContactIds((prev) => {
        const next = new Set(prev);
        next.delete(contactToDelete.ItemId);
        return next;
      });
      setIsDeleteConfirmOpen(false);
      setContactToDelete(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete contact',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const openRemoveFromList = (contact: Contact) => {
    setIsBulkRemoveFromList(false);
    setActionContact(contact);
    setIsRemoveFromListConfirmOpen(true);
  };

  const openBulkRemoveFromList = () => {
    if (selectedCount === 0 || !activeList) return;
    setIsBulkRemoveFromList(true);
    setActionContact(null);
    setIsRemoveFromListConfirmOpen(true);
  };

  const tableColSpan = visibleColumns.length + 2;
  const isTableLoading = (isLoading || isFetching) && visibleContacts.length === 0;
  const skeletonRowCount = Math.max(6, Math.min(pageSize, 12));
  const hasQueryConstraints =
    Boolean(search.trim()) ||
    filtersActive ||
    channelFilterChips.length > 0 ||
    advancedFilterRowCount > 0;
  const tableStateKind = isTableLoading
    ? null
    : isError
      ? 'error'
      : visibleContacts.length === 0
        ? hasQueryConstraints
          ? 'noresults'
          : 'empty'
        : null;
  const tableCellContext = useMemo<ContactsTableCellContext>(
    () => ({
      isListFilterActive,
      isRemovingFromList: isRemovingFromListAction,
      listName: filters.listName,
      onOpenEditContact: openEditContact,
      onOpenRemoveFromList: openRemoveFromList,
      onOpenDeleteContact: openDeleteContact,
      onCopyContactId: copyContactId,
    }),
    [filters.listName, isListFilterActive, isRemovingFromListAction, copyContactId]
  );

  const handleAddToLists = (lists: string[]) => {
    if (isBulkAction) {
      const added = lists.filter(Boolean);
      toast({
        variant: 'success',
        title: 'Added to list',
        description: `${selectedCount} contact${selectedCount === 1 ? '' : 's'} ${
          added.length > 0 ? `added to ${added.join(', ')}.` : 'updated.'
        }`,
      });
      clearSelection();
      setIsAddToListOpen(false);
      return;
    }

    if (!actionContact) return;
    const displayName = getContactDisplayName(actionContact);
    const previous = getContactLists(actionContact);
    const added = lists.filter((list) => !previous.includes(list));

    toast({
      variant: 'success',
      title: added.length > 0 ? 'Added to list' : 'Lists updated',
      description:
        added.length > 0
          ? `${displayName} was added to ${added.join(', ')}.`
          : `List membership updated for ${displayName}.`,
    });
    setActionContact(null);
  };

  const addToListLabel = isBulkAction
    ? `${selectedCount.toLocaleString()} contact${selectedCount === 1 ? '' : 's'}`
    : actionContact
      ? getContactDisplayName(actionContact)
      : '';

  const handleRemoveFromList = async () => {
    if (!activeList) return;

    const listName = activeList.name.trim() || filters.listName || 'this list';
    const contactIds = isBulkRemoveFromList
      ? Array.from(selectedContactIds)
      : actionContact
        ? [actionContact.ItemId]
        : [];

    if (contactIds.length === 0) return;

    try {
      await removeListMembers({
        listId: activeList.id,
        contactIds,
      });

      if (isBulkRemoveFromList) {
        toast({
          variant: 'success',
          title: 'Removed from list',
          description: `${contactIds.length} contact${contactIds.length === 1 ? '' : 's'} ${
            contactIds.length === 1 ? 'was' : 'were'
          } removed from ${listName}.`,
        });
        clearSelection();
        setIsBulkRemoveFromList(false);
      } else if (actionContact) {
        toast({
          variant: 'success',
          title: 'Removed from list',
          description: `${getContactDisplayName(actionContact)} was removed from ${listName}.`,
        });
        setActionContact(null);
      }

      setIsRemoveFromListConfirmOpen(false);
    } catch (removeError) {
      toast({
        variant: 'destructive',
        title: 'Failed to remove from list',
        description:
          removeError instanceof Error
            ? removeError.message
            : 'Could not remove the contact from this list.',
      });
    }
  };

  const removeFromListConfirmDescription = isBulkRemoveFromList
    ? `Are you sure you want to remove ${selectedCount} selected contact${
        selectedCount === 1 ? '' : 's'
      } from ${activeList?.name.trim() || filters.listName || 'this list'}?`
    : actionContact
      ? `Are you sure you want to remove ${getContactDisplayName(actionContact)} from ${
          activeList?.name.trim() || filters.listName || 'this list'
        }?`
      : 'Are you sure you want to remove this contact from the list?';

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-full flex-col gap-5 overflow-x-hidden">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">Contacts</h1>
          <p className="mt-0.5 text-sm text-neutral-600">
            {`${totalCount.toLocaleString()} contact${totalCount === 1 ? '' : 's'} · ${listsCount.toLocaleString()} list${listsCount === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-10 gap-2 border-neutral-300 px-4 text-neutral-900 hover:bg-neutral-100"
            onClick={() => setIsExportOpen(true)}
          >
            <Export className="size-4" />
            Export
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-10 gap-2 border-neutral-300 px-4 text-neutral-900 hover:bg-neutral-100"
            onClick={() => navigate('/contacts/import')}
          >
            <DownloadSimpleIcon className="size-4" />
            Import
          </Button>
          <Button
            type="button"
            className="h-10 gap-2 bg-primary-600 px-4 text-white shadow-e1 hover:bg-primary-700"
            onClick={() => setIsContactDrawerOpen(true)}
          >
            <PlusIcon className="size-4" />
            Add contact
          </Button>
        </div>
      </div>

      {membershipJob ? (
        <ListMembershipJobProgress
          jobId={membershipJob.jobId}
          listNamesById={listNamesById}
          onDismiss={() => setMembershipJob(null)}
        />
      ) : null}

      {selectedCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5">
          <span className="font-medium text-primary-700">
            {selectAllMatching
              ? `All ${selectedCount.toLocaleString()} matching selected`
              : `${selectedCount} selected`}
          </span>
          {canSelectAllMatching ? (
            <button
              type="button"
              className="text-xs font-medium text-primary-700 underline hover:text-primary-800"
              onClick={selectAllMatchingContacts}
            >
              Select all {totalCount.toLocaleString()} matching contacts
            </button>
          ) : null}
          <div className="h-5 w-px bg-primary-200" />
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-700 hover:text-neutral-900"
            onClick={openBulkAddToList}
          >
            <ListPlusIcon className="size-4" />
            Add to list
          </button>
          {selectAllMatching ? (
            // Only adding to lists runs for a whole filter; the other bulk actions still work on the
            // rows of this page, so they are offered once "select all" is cleared.
            <span className="text-xs text-neutral-500">
              Other bulk actions apply to one page — clear to use them.
            </span>
          ) : isListFilterActive ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-700 hover:text-neutral-900"
              onClick={openBulkRemoveFromList}
              disabled={isRemovingFromListAction}
            >
              <FolderMinusIcon className="size-4" />
              Remove from {filters.listName}
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-700 hover:text-neutral-900"
              onClick={openBulkSuppress}
            >
              <ProhibitIcon className="size-4" />
              Suppress
            </button>
          )}
          {selectAllMatching ? null : (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive hover:opacity-80 disabled:opacity-50"
              onClick={openBulkDelete}
              disabled={isDeletingContacts}
            >
              <TrashIcon className="size-4" />
              Delete
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            className="text-xs text-neutral-600 hover:text-neutral-900"
            onClick={clearSelection}
          >
            Clear
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-500 focus-within:border-primary-600 focus-within:ring-2 focus-within:ring-primary-600/30">
          <MagnifyingGlassIcon className="size-4 shrink-0" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search contacts by name, email, or phone"
            className="h-auto border-0 bg-transparent p-0 text-sm text-neutral-900 shadow-none placeholder:text-neutral-500 focus-visible:ring-0"
            aria-label="Search contacts by name, email, or phone"
          />
          {search.trim() ? (
            <button
              type="button"
              className="text-neutral-400 hover:text-neutral-700"
              aria-label="Clear search"
              onClick={() => setSearch('')}
            >
              <XIcon className="size-3.5" />
            </button>
          ) : null}
        </div>

        <ContactsFiltersDropdown
          emailStatuses={emailStatusFilters}
          smsStatuses={smsStatusFilters}
          listName={filters.listName}
          customFields={filterableCustomFields}
          fieldFilters={filters.fields}
          onApply={handleApplyFilters}
        />

        <ContactsColumnConfigDropdown
          toggleableColumns={toggleableColumns}
          columnVisibility={columnVisibility}
          onToggleColumn={toggleColumnVisibility}
        />
      </div>

      {(filterChips.length > 0 || channelFilterChips.length > 0 || advancedFilterRowCount > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {channelFilterChips.map((chip) => (
            <Badge
              key={chip.id}
              variant="secondary"
              className="gap-1 rounded-md bg-primary-50 px-2.5 py-1 text-sm font-medium text-primary-600 hover:bg-primary-50"
            >
              {chip.label}
              <button
                type="button"
                className="rounded-sm hover:bg-primary-100"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => {
                  if (chip.type === 'email') {
                    setEmailStatusFilters((prev) => prev.filter((value) => value !== chip.value));
                  } else {
                    setSmsStatusFilters((prev) => prev.filter((value) => value !== chip.value));
                  }
                }}
              >
                <XIcon className="size-3.5" />
              </button>
            </Badge>
          ))}
          {filterChips.map((chip) => (
            <Badge
              key={chip.fieldKey}
              variant="secondary"
              className="gap-1 rounded-md bg-primary-50 px-2.5 py-1 text-sm font-medium text-primary-600 hover:bg-primary-50"
            >
              {chip.label}
              <button
                type="button"
                className="rounded-sm hover:bg-primary-100"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => handleRemoveFilter(chip.fieldKey)}
              >
                <XIcon className="size-3.5" />
              </button>
            </Badge>
          ))}
          {advancedFilterRowCount > 0 ? (
            <Badge
              variant="secondary"
              className="rounded-md bg-primary-50 px-2.5 py-1 text-sm font-medium text-primary-600"
            >
              Advanced filters ({advancedFilterRowCount})
            </Badge>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-primary-600 hover:bg-primary-50 hover:text-primary-700"
            onClick={() => {
              handleClearFilters();
              setAdvancedRows([{ id: '1', fieldKey: '', operator: '', value: '' }]);
              setAdvancedMatchType('all');
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
        <div className="min-h-0 flex-1 overflow-auto">
          <Table className="h-auto min-w-[1000px] w-full border-collapse [&_tbody_td]:align-top">
            <TableHeader>
              <TableRow className="border-b border-neutral-200 hover:bg-transparent">
                <TableHead className={`${CONTACTS_TABLE_CHECKBOX_STICKY_CLASS} top-0 px-4 py-3`}>
                  <Checkbox
                    checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                    onCheckedChange={(checked) => toggleAllPageSelection(checked === true)}
                    aria-label="Select all contacts on this page"
                    className="border-neutral-300 data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-600"
                  />
                </TableHead>
                {visibleColumns.map((column) => (
                  <TableHead
                    key={column.id}
                    className={getContactsTableHeaderCellClass(column.id, column.widthClass)}
                  >
                    {column.label}
                  </TableHead>
                ))}
                <TableHead className="sticky top-0 z-10 w-12 bg-neutral-50 px-4 py-3" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isTableLoading ? (
                <ContactsTableSkeletonRows
                  rowCount={skeletonRowCount}
                  visibleColumns={visibleColumns}
                />
              ) : null}

              {tableStateKind ? (
                <ContactsTableStateRow
                  kind={tableStateKind}
                  colSpan={tableColSpan}
                  onRetry={() => void refetch()}
                  onAddContact={() => setIsContactDrawerOpen(true)}
                  onImport={() => navigate('/contacts/import')}
                  onClearFilters={handleClearAllQuery}
                />
              ) : null}

              {!tableStateKind &&
                visibleContacts.map((contact) => {
                  const isSelected = isContactSelected(contact.ItemId);
                  return (
                    <TableRow
                      key={contact.ItemId}
                      className={`group cursor-pointer border-b border-neutral-100 hover:bg-neutral-50 ${
                        isSelected ? 'bg-primary-50 hover:bg-primary-50' : ''
                      }`}
                      onClick={() => navigate(`/contacts/${contact.ItemId}`)}
                    >
                      <TableCell
                        className={`${CONTACTS_TABLE_CHECKBOX_STICKY_CLASS} px-4 py-3 align-middle ${
                          isSelected ? 'bg-primary-50' : 'bg-white group-hover:bg-neutral-50'
                        }`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            toggleContactSelection(contact.ItemId, checked === true)
                          }
                          aria-label={`Select ${getContactDisplayName(contact)}`}
                          className="border-neutral-300 data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-600"
                        />
                      </TableCell>
                      {visibleColumns.map((column) => (
                        <TableCell
                          key={column.id}
                          className={getContactsTableBodyCellClass(column.id, column.widthClass, {
                            selected: isSelected,
                          })}
                        >
                          {renderContactsTableCell(column.id, contact, customFieldDefinitions)}
                        </TableCell>
                      ))}
                      <ContactsTableActionsCell contact={contact} context={tableCellContext} />
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        <ContactsPagination
          pageNo={pageNo}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          totalUnfiltered={data?.totalUnfiltered}
          hasNextPage={Boolean(data?.hasNextPage)}
          hasPreviousPage={Boolean(data?.hasPreviousPage)}
          onPageChange={setPageNo}
          onPageSizeChange={handlePageSizeChange}
          variant="simple"
          isLoading={isTableLoading}
          isError={isError && !isTableLoading}
        />
      </div>

      <AdvancedFiltersDialog
        open={isAdvancedFiltersOpen}
        onOpenChange={setIsAdvancedFiltersOpen}
        rows={advancedRows}
        matchType={advancedMatchType}
        onApply={(rows, matchType) => {
          setAdvancedRows(rows);
          setAdvancedMatchType(matchType);
        }}
      />
      <ContactDrawer
        open={isContactDrawerOpen || Boolean(contactToEdit)}
        onOpenChange={(open) => {
          if (!open) {
            setIsContactDrawerOpen(false);
            setContactToEdit(null);
          }
        }}
        contact={contactToEdit}
      />
      <AddToListModal
        open={isAddToListOpen}
        onOpenChange={(open) => {
          setIsAddToListOpen(open);
          if (!open) {
            setActionContact(null);
            setIsBulkAction(false);
          }
        }}
        contactName={addToListLabel}
        contactId={isBulkAction ? undefined : actionContact?.ItemId}
        contactIds={isBulkAction && !selectAllMatching ? Array.from(selectedContactIds) : undefined}
        currentLists={isBulkAction || !actionContact ? [] : getContactLists(actionContact)}
        onAdd={handleAddToLists}
        selectAll={
          isBulkAction && selectAllMatching
            ? { snapshot: exportSnapshot, excludeContactIds: Array.from(excludedContactIds) }
            : undefined
        }
        onJobStarted={(job) => {
          setMembershipJob({ jobId: job.jobId });
          clearSelection();
        }}
      />
      <ConfirmationModal
        open={isBulkSuppressConfirmOpen}
        onOpenChange={(open) => {
          if (isBulkSuppressing) return;
          setIsBulkSuppressConfirmOpen(open);
          if (!open) bulkSuppressIdempotencyKeyRef.current = null;
        }}
        title="Suppress selected contacts"
        description={`Are you sure you want to suppress ${selectedCount} selected contact${
          selectedCount === 1 ? '' : 's'
        }? They will be added to the suppression list and won't receive email campaigns.`}
        confirmText={isBulkSuppressing ? 'Suppressing…' : 'Suppress'}
        cancelText="Cancel"
        confirmClassName="bg-destructive text-white"
        confirmDisabled={isBulkSuppressing}
        preventAutoClose
        onConfirm={() => {
          void handleBulkSuppress();
        }}
      />
      <ConfirmationModal
        open={isRemoveFromListConfirmOpen}
        onOpenChange={(open) => {
          if (isRemovingFromListAction) return;
          setIsRemoveFromListConfirmOpen(open);
          if (!open) {
            setActionContact(null);
            setIsBulkRemoveFromList(false);
          }
        }}
        title={isBulkRemoveFromList ? 'Remove contacts from list' : 'Remove from list'}
        description={removeFromListConfirmDescription}
        confirmText={isRemovingFromListAction ? 'Removing...' : 'Remove'}
        cancelText="Cancel"
        confirmClassName="bg-destructive text-white"
        preventAutoClose
        onConfirm={handleRemoveFromList}
      />
      <ConfirmationModal
        open={isBulkDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (isDeletingContacts) return;
          setIsBulkDeleteConfirmOpen(open);
        }}
        title="Delete selected contacts"
        description={`Are you sure you want to delete ${selectedCount} selected contact${
          selectedCount === 1 ? '' : 's'
        }? This action cannot be undone.`}
        confirmText={isDeletingContacts ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        confirmClassName="bg-destructive text-white"
        preventAutoClose
        onConfirm={() => {
          void handleBulkDelete();
        }}
      />
      <ConfirmationModal
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (isDeletingContact) return;
          setIsDeleteConfirmOpen(open);
          if (!open) setContactToDelete(null);
        }}
        title="Delete this contact"
        description={`Are you sure you want to delete "${
          contactToDelete ? getContactDisplayName(contactToDelete) : 'this contact'
        }"? This action cannot be undone.`}
        confirmText={isDeletingContact ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        confirmClassName="bg-destructive text-white"
        preventAutoClose
        onConfirm={() => {
          void handleDeleteContact();
        }}
      />
      <ExportContactsDialog
        open={isExportOpen}
        onOpenChange={setIsExportOpen}
        totalCount={totalCount}
        search={search}
        activeFilterCount={activeFilterCount}
        snapshot={exportSnapshot}
        visibleColumns={visibleColumns}
        customFieldDefinitions={customFieldDefinitions}
      />
    </div>
  );
};
